import { useMemo, useState, ReactNode, useContext, createContext } from 'react';
import { Box, Backdrop, Typography, CircularProgress } from '@mui/material';
import { useTranslate } from 'src/locales';

type LoadingContextType = {
  showLoading: (message?: string) => void;
  hideLoading: () => void;
};

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

  const { t: tCommon } = useTranslate('common');

  const showLoading = (message?: string) => {
    setLoading(true);
    setLoadingMessage(message || tCommon('loading'));
  };
  const hideLoading = () => {
    setLoading(false);
    setLoadingMessage(null);
  };

  return (
    <LoadingContext.Provider
      value={useMemo(() => ({ showLoading, hideLoading }), [showLoading, hideLoading])}
    >
      {loading && (
        <Backdrop sx={{ color: '#fff', zIndex: 1301, flexDirection: 'column' }} open>
          <CircularProgress color="inherit" />
          <Box mt={2}>
            <Typography variant="h6">{loadingMessage}</Typography>
          </Box>
        </Backdrop>
      )}
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
